import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Download, ArrowRight, Shield, MapPin, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFFERS = [
    {
        id: 'starter',
        name: 'STARTER',
        tagline: 'Decouverte',
        target: 'Petites communes < 3 500 hab',
        format: '100% en ligne, autonomie',
        price: '49 - 99',
        priceUnit: 'EUR / mois',
        setup: 'Setup inclus',
        color: 'border-zinc-500',
        accent: 'text-zinc-300',
        highlight: false,
        features: [
            'Console CIVIS+ dediee',
            'Setup guide (videos + doc)',
            'Saisie autonome du contexte',
            '1 exercice "a blanc" / an',
            'Support email (48h)',
        ],
    },
    {
        id: 'pro',
        name: 'PRO',
        tagline: 'Conformite PCS',
        target: 'Communes 3 500 - 20 000 hab',
        format: 'Hybride, accompagnement a distance',
        price: '199 - 399',
        priceUnit: 'EUR / mois',
        setup: 'Setup 1 500 - 3 000 EUR',
        color: 'border-blue-500',
        accent: 'text-blue-400',
        highlight: true,
        badge: 'Le plus choisi',
        features: [
            'Tout Starter, plus :',
            'Setup accompagne en visio',
            'Import de votre PCS',
            'Cartographie risques DDRM',
            'Formation operateurs',
            '1 exercice reel encadre / an',
            'Replay timeline + debrief PDF',
            'Support prioritaire (24h)',
        ],
    },
    {
        id: 'expert',
        name: 'EXPERT',
        tagline: 'Crise terrain',
        target: 'Grandes communes / EPCI / sites a risques',
        format: 'Presentiel + accompagnement',
        price: '599 - 1 200',
        priceUnit: 'EUR / mois',
        setup: 'Accompagnement 5-10k EUR / an',
        color: 'border-cyan-500',
        accent: 'text-cyan-400',
        highlight: false,
        features: [
            'Tout Pro, plus :',
            'Setup presentiel (audit terrain)',
            'Cartographie GPS sites critiques',
            'Integration SDIS + Police locale',
            'Formation immersive equipe PCS',
            '2 a 3 exercices reels / an',
            'Rapport analyse ecrit par ex.',
            'Astreinte 7j/7 periodes a risque',
            'Page citoyen optionnelle',
        ],
    },
    {
        id: 'enterprise',
        name: 'ENTERPRISE',
        tagline: 'Sur-mesure',
        target: 'Metropoles, departements, sites sensibles',
        format: 'Cellule dediee',
        price: 'Sur devis',
        priceUnit: 'a partir de 5 000 EUR / mois',
        setup: 'Inclus dans la prestation',
        color: 'border-violet-500',
        accent: 'text-violet-400',
        highlight: false,
        features: [
            'Tout Expert, plus :',
            'Instance dediee (cloud / on-premise)',
            'Modules custom (ORSEC, FR-Alert)',
            'Exercices multi-acteurs',
            'Consultant CIVIS+ dedie',
            'SLA 99.95% + audit annuel',
        ],
    },
];

const COMPARISON = [
    ['Console CIVIS+ multi-tenant', true, true, true, true],
    ['Multi-utilisateurs avec roles', true, true, true, true],
    ['Journal de bord + auto-journal', true, true, true, true],
    ['Cartographie dynamique', true, true, true, true],
    ['Annuaire de crise + import CSV', true, true, true, true],
    ['Generation PDF (arretes, fiches)', true, true, true, true],
    ['Setup a distance accompagne', false, true, true, true],
    ['Import de votre PCS existant', false, true, true, true],
    ['Formation operateurs', 'Videos', 'Visio', 'Presentiel', 'Sur mesure'],
    ['Exercices reels / an', '1 blanc', '1', '2-3', 'Multi-acteurs'],
    ['Replay timeline + debrief PDF', false, true, true, true],
    ['Setup en presentiel', false, false, true, true],
    ['Astreinte periodes a risque', false, false, true, true],
    ['Instance dediee / on-premise', false, false, false, true],
    ['Modules custom (ORSEC, FR-Alert)', false, false, false, true],
    ['Consultant CIVIS+ dedie nomme', false, false, false, true],
];

const ADDONS = [
    ['Exercice supplementaire encadre', '800 - 1 500 EUR / session'],
    ['Migration PCS Word/Excel existant', '500 - 1 500 EUR'],
    ['Personnalisation graphique (logo, charte)', '300 - 500 EUR'],
    ['Audit annuel post-saison a risque', '1 500 - 3 000 EUR'],
    ['Module elus (vue mobile simplifiee)', '30 EUR / mois'],
];

const PricingPage = () => {
    const renderCell = (v) => {
        if (v === true) return <Check className="w-5 h-5 text-emerald-400 mx-auto" />;
        if (v === false) return <X className="w-4 h-4 text-zinc-700 mx-auto" />;
        return <span className="text-xs text-zinc-300">{v}</span>;
    };

    return (
        <div className="min-h-screen bg-zinc-950 text-white" data-testid="pricing-page">
            {/* Top bar */}
            <header className="border-b border-zinc-800 bg-zinc-900/80 backdrop-blur sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="logo-home-link">
                        <div className="px-3 py-1.5 bg-zinc-800 rounded-md flex items-baseline gap-0.5">
                            <span className="font-bold text-lg tracking-tight">CIVIS</span>
                            <span className="text-blue-400 font-bold text-xl">+</span>
                        </div>
                    </Link>
                    <div className="flex items-center gap-3">
                        <a href={`${API}/marketing/brochure`} target="_blank" rel="noopener noreferrer"
                            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm text-zinc-300 border border-zinc-700 hover:bg-zinc-800 transition"
                            data-testid="brochure-pdf-btn">
                            <Download className="w-4 h-4" /> Plaquette PDF
                        </a>
                        <Link to="/login">
                            <Button className="bg-blue-600 hover:bg-blue-500" data-testid="pricing-login-btn">Se connecter</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-7xl mx-auto px-6 pt-16 pb-12 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs mb-5">
                    Plateforme dediee aux communes francaises
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4 leading-tight">
                    Equipez votre <span className="text-blue-400">cellule de crise</span><br />
                    avec un outil professionnel
                </h1>
                <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto">
                    4 niveaux d'offre, du setup autonome a l'accompagnement sur le terrain.
                    Choisissez celui qui correspond a votre commune.
                </p>
                <div className="flex justify-center gap-3 mt-8 flex-wrap">
                    <a href={`${API}/marketing/brochure`} target="_blank" rel="noopener noreferrer">
                        <Button size="lg" variant="outline" className="border-zinc-700 hover:bg-zinc-800" data-testid="hero-brochure-btn">
                            <Download className="w-4 h-4 mr-2" /> Telecharger la plaquette PDF
                        </Button>
                    </a>
                    <a href="mailto:contact@civis-plus.fr?subject=Demande%20de%20devis%20CIVIS%2B">
                        <Button size="lg" className="bg-blue-600 hover:bg-blue-500" data-testid="hero-contact-btn">
                            Demander un devis <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </a>
                </div>
            </section>

            {/* Offer cards */}
            <section className="max-w-7xl mx-auto px-6 pb-20">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                    {OFFERS.map(o => (
                        <div key={o.id}
                            data-testid={`offer-card-${o.id}`}
                            className={`relative rounded-xl border-2 ${o.color} bg-zinc-900/70 p-6 flex flex-col ${o.highlight ? 'shadow-[0_0_40px_-10px_rgba(59,130,246,0.5)] scale-[1.02]' : ''}`}>
                            {o.badge && (
                                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-blue-500 text-white text-[10px] font-semibold uppercase tracking-wider">
                                    {o.badge}
                                </span>
                            )}
                            <div className="mb-4">
                                <p className={`text-xs font-semibold uppercase tracking-wider ${o.accent}`}>{o.tagline}</p>
                                <h3 className="text-2xl font-bold mt-1">CIVIS+ {o.name}</h3>
                            </div>
                            <div className="pb-4 border-b border-zinc-800 space-y-1">
                                <p className="text-xs text-zinc-400 flex items-start gap-1.5"><Users className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> <span>{o.target}</span></p>
                                <p className="text-xs text-zinc-500 flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> <span>{o.format}</span></p>
                            </div>
                            <div className="py-4">
                                <p className="text-3xl font-bold tracking-tight">{o.price}</p>
                                <p className="text-xs text-zinc-500 mt-0.5">{o.priceUnit}</p>
                                <p className="text-[11px] text-zinc-500 mt-2">{o.setup}</p>
                            </div>
                            <ul className="space-y-2 mb-6 flex-1">
                                {o.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${o.accent}`} />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <a href={`mailto:contact@civis-plus.fr?subject=Demande%20${o.name}`}>
                                <Button
                                    className={`w-full ${o.highlight ? 'bg-blue-600 hover:bg-blue-500' : 'bg-zinc-800 hover:bg-zinc-700 border border-zinc-700'}`}
                                    data-testid={`offer-cta-${o.id}`}>
                                    {o.id === 'enterprise' ? 'Nous contacter' : 'Demander un devis'}
                                </Button>
                            </a>
                        </div>
                    ))}
                </div>
            </section>

            {/* Comparison table */}
            <section className="max-w-7xl mx-auto px-6 pb-20" data-testid="comparison-section">
                <div className="text-center mb-10">
                    <h2 className="text-3xl sm:text-4xl font-bold mb-3">Comparatif detaille</h2>
                    <p className="text-zinc-400">Toutes les fonctionnalites incluses dans chaque offre.</p>
                </div>
                <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-900/50">
                    <table className="w-full">
                        <thead>
                            <tr>
                                <th className="text-left p-4 bg-zinc-900 text-sm font-semibold sticky left-0 z-10 min-w-[260px]">Fonctionnalite</th>
                                <th className="p-4 bg-zinc-700 text-xs font-bold uppercase tracking-wider min-w-[110px]">Starter</th>
                                <th className="p-4 bg-blue-600 text-xs font-bold uppercase tracking-wider min-w-[110px]">Pro</th>
                                <th className="p-4 bg-cyan-600 text-xs font-bold uppercase tracking-wider min-w-[110px]">Expert</th>
                                <th className="p-4 bg-violet-600 text-xs font-bold uppercase tracking-wider min-w-[110px]">Enterprise</th>
                            </tr>
                        </thead>
                        <tbody>
                            {COMPARISON.map(([label, v1, v2, v3, v4], i) => (
                                <tr key={label} className={`border-t border-zinc-800 ${i % 2 === 0 ? 'bg-zinc-900/30' : ''}`}>
                                    <td className="p-3.5 text-sm text-zinc-200 sticky left-0 bg-inherit">{label}</td>
                                    <td className="p-3.5 text-center">{renderCell(v1)}</td>
                                    <td className="p-3.5 text-center">{renderCell(v2)}</td>
                                    <td className="p-3.5 text-center">{renderCell(v3)}</td>
                                    <td className="p-3.5 text-center">{renderCell(v4)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Addons */}
            <section className="max-w-7xl mx-auto px-6 pb-20">
                <div className="text-center mb-10">
                    <h2 className="text-3xl sm:text-4xl font-bold mb-3">Options et services additionnels</h2>
                    <p className="text-zinc-400">Disponibles pour toutes les offres.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                    {ADDONS.map(([label, price]) => (
                        <div key={label} className="flex items-center justify-between p-4 rounded-lg border border-zinc-800 bg-zinc-900/50">
                            <span className="text-sm text-zinc-200">{label}</span>
                            <span className="text-sm font-mono text-blue-400 font-semibold ml-3 flex-shrink-0">{price}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Final CTA */}
            <section className="max-w-4xl mx-auto px-6 pb-24">
                <div className="rounded-2xl border-2 border-blue-500/30 bg-gradient-to-br from-blue-950/60 to-zinc-900 p-10 text-center">
                    <Shield className="w-10 h-10 text-blue-400 mx-auto mb-4" />
                    <h2 className="text-2xl sm:text-3xl font-bold mb-3">Une question ? Un cas particulier ?</h2>
                    <p className="text-zinc-400 mb-6 max-w-xl mx-auto">
                        Nous proposons des demonstrations gratuites pour les maires et DGS qui souhaitent
                        evaluer CIVIS+ dans leur contexte.
                    </p>
                    <div className="flex justify-center gap-3 flex-wrap">
                        <a href="mailto:contact@civis-plus.fr?subject=Demande%20de%20demonstration%20CIVIS%2B">
                            <Button size="lg" className="bg-blue-600 hover:bg-blue-500" data-testid="final-cta-demo">
                                Demander une demonstration <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </a>
                        <a href={`${API}/marketing/brochure`} target="_blank" rel="noopener noreferrer">
                            <Button size="lg" variant="outline" className="border-zinc-700 hover:bg-zinc-800" data-testid="final-cta-pdf">
                                <Download className="w-4 h-4 mr-2" /> Telecharger la plaquette PDF
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-zinc-800 py-8 text-center text-xs text-zinc-500">
                CIVIS+ - Plateforme de gestion de crise communale - {new Date().getFullYear()}
            </footer>
        </div>
    );
};

export default PricingPage;
